import { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';
import './HeroSection.css';

gsap.registerPlugin(ScrollTrigger);

// Feature callouts shown WHILE scrolling THROUGH each clip (not at joins),
// so the footage isn't silent. One per clip; null = no callout (clip 1 is
// covered by the hero headline). Positioned bottom-left, off the bike.
const CLIP_NOTES = [
  // VIDEO 1 — appears near the clip's end, clears before the EV-bike stop.
  { eyebrow: 'First Look', title: 'The EVPD Scooter', sub: 'Electric, silent and sculpted — the next ride, revealed.', late: true },
  { eyebrow: 'Performance', title: 'Instant Torque', sub: '0–60 km/h in 3.8s — silent, seamless, immediate.' },
  { eyebrow: 'EV Charging', title: 'Charge Anywhere', sub: 'Smart fast-charging stations across the network — power in minutes, not hours.' },
  { eyebrow: 'Intelligence', title: 'Always Connected', sub: 'Live navigation, ride telemetry and OTA updates.', fadeOut: 5 },
];

// Mid-clip STOPS — the scroll PAUSES on a frame INSIDE a clip, holds, and
// waterfalls a full card in (a chapter stop in the middle of a clip, not at a
// boundary). Keyed by clip index; `at` is the normalized position [0..1] where
// the clip freezes (tune it to land on the exact frame).
const CLIP_STOPS = [
  null, null, null, null,
  { // VIDEO 5 — pause on the battery-swap frame and tell the swappable story
    at: 0.5,
    eyebrow: 'Battery',
    align: 'left',
    title: 'Swappable Power',
    cta: 'About the Battery',
    sub: 'Swap an empty pack for a fresh one in seconds — never tethered to a socket. Carry your power with you and keep moving.',
  },
];

// Active clip-join transition:
//   'dark'  = dark cinematic veil (best backdrop for the light premium text)
//   'fog'   = smoke sweep   |   'bloom' = light glare
// Only the chosen overlay is rendered — the others cost nothing.
const JOIN_TRANSITION = 'dark';

// Dot-nav labels — one per clip/scene, shown on hover. Edit freely.
const DOT_NAMES = ['The Reveal', 'The EV Bike', 'Charging', 'Smart Helmet', 'Battery', 'Fast Charging', 'The Network', 'Clean Energy'];

// Editorial text shown DURING each clip join (one entry per boundary).
// Join 0 = between clip 1 → 2, Join 1 = between clip 2 → 3. Edit freely.
const JOIN_TEXTS = [
  {
    // shown on the EV BIKE frame
    eyebrow: 'The EV Bike',
    align: 'left',
    highlight: { x: '59%', y: '57%' },
    title: 'Engineered to Move',
    cta: 'Explore the Design',
    sub: 'Meet the EVPD electric scooter — every surface sculpted for flow and presence. Instant electric torque, a low centre of gravity and a silent, seamless ride: performance and elegance engineered into one machine.',
    stats: [
      { value: '0.28', label: 'Drag Cd' },
      { value: 'Matrix', label: 'LED Lighting' },
      { value: 'Forged', label: 'Alloy Frame' },
    ],
  },
  {
    // shown on the EV CHARGING STATION frame (wall charger)
    eyebrow: 'Charging',
    align: 'right',
    highlight: { x: '24%', y: '52%' },
    title: 'Power at the Wall',
    cta: 'See Charging',
    sub: 'A sleek wall-mounted charger brings effortless power to your space. Plug in overnight and wake to a full battery — smart, fast and beautifully simple.',
    stats: [
      { value: '4 hrs', label: 'Full Charge' },
      { value: 'Smart', label: 'App Control' },
      { value: 'IP65', label: 'All-Weather' },
    ],
  },
  {
    eyebrow: 'Intelligence',
    align: 'right',   // helmet sits center-left in this frame
    highlight: { x: '40%', y: '46%' },
    title: 'See Beyond',
    cta: 'Inside the HUD',
    sub: 'Step inside the smart helmet and the connected ride. A heads-up display projects navigation, speed and battery straight into your view — eyes on the road, always. Bluetooth, live telemetry and over-the-air updates keep you connected to the world and the machine.',
    stats: [
      { value: 'HUD', label: 'Heads-Up Display' },
      { value: 'Live', label: 'Navigation' },
      { value: '4G', label: 'Always Connected' },
    ],
  },
  // BATTERY-SWAP (video5): no start card — the swappable-battery matter is
  // shown MID-CLIP as a callout while the swap happens (see CLIP_NOTES[4]).
  // This also makes video4 → video5 a continuous flow (no stop).
  null,
  {
    // EV CHARGER used by both an e-bike and a car
    eyebrow: 'Fast Charging',
    align: 'left',
    highlight: { x: '62%', y: '50%' },
    title: 'Charge Together',
    cta: 'The Network',
    sub: 'Public fast-charging for every electric vehicle — scooter and car, side by side. A shared network powering the shift to electric across the whole city.',
    stats: [
      { value: '150kW', label: 'DC Fast Charge' },
      { value: 'Bike + Car', label: 'Any EV' },
      { value: '24/7', label: 'Network' },
    ],
  },
  {
    // VIDEO 7 — aerial / city-wide view of the EV ecosystem
    eyebrow: 'The Network',
    align: 'left',
    title: 'A City, Electrified',
    cta: 'The Vision',
    sub: 'Seen from above, the shift is everywhere — charging points, swap stations and connected riders woven into the streets. Not a single product, but a city quietly going electric.',
    stats: [
      { value: 'City-Wide', label: 'Coverage' },
      { value: 'Connected', label: 'Infrastructure' },
      { value: 'Zero', label: 'Emissions' },
    ],
  },
  {
    // VIDEO 8 — STOP at its start: the clean-energy loop (solar → grid → charge)
    eyebrow: 'Clean Energy',
    align: 'left',
    title: 'Sun to Charge',
    cta: 'The Energy Loop',
    sub: 'Rooftop solar panels feed the grid, and that clean power runs our EV charging stations. Every ride is drawn from sunlight — a closed loop from panel, to grid, to plug.',
    stats: [
      { value: 'Solar', label: 'Generation' },
      { value: 'Grid-Tied', label: 'Distribution' },
      { value: '100%', label: 'Clean Charge' },
    ],
  },
];

const HeroSection = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  // Close menu and re-enable scroll
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const sectionRef     = useRef(null);
  const videoRef       = useRef(null);
  const video2Ref      = useRef(null);
  const video3Ref      = useRef(null);
  const video4Ref      = useRef(null);
  const video5Ref      = useRef(null);
  const video6Ref      = useRef(null);
  const video7Ref      = useRef(null);
  const video8Ref      = useRef(null);
  const navRef         = useRef(null);
  const eyebrowRef     = useRef(null);
  const headlineRef    = useRef(null);
  const descRef        = useRef(null);
  const ctaRef         = useRef(null);
  const accentLineRef  = useRef(null);
  const scrollHintRef  = useRef(null);
  const heroContentRef = useRef(null);
  const transitionRef  = useRef(null);
  const videoWrapRef   = useRef(null);
  const fogRef         = useRef(null);
  const bloomRef       = useRef(null);
  const joinTextRefs   = useRef([]);
  const highlightRefs  = useRef([]);
  const clipNoteRefs   = useRef([]);
  const clipStopRefs   = useRef([]);
  const dotRefs        = useRef([]);
  const scrollToClipRef = useRef(null);
  const triggersRef    = useRef([]);
  const lenisRef       = useRef(null);
  const rafFnRef       = useRef(null);

  useEffect(() => {
    // ── Lenis smooth scroll ──────────────────────────────────────
    const lenis = new Lenis({
      duration: 1.2,          // more smoothing — fast flicks ease in gently
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 0.7,   // each wheel tick scrolls less → fast scroll advances slowly
      touchMultiplier: 1.1,
    });
    lenisRef.current = lenis;

    lenis.on('scroll', ScrollTrigger.update);
    rafFnRef.current = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(rafFnRef.current);
    gsap.ticker.lagSmoothing(0);

    const video   = videoRef.current;
    const video2  = video2Ref.current;
    const video3  = video3Ref.current;
    const video4  = video4Ref.current;
    const video5  = video5Ref.current;
    const video6  = video6Ref.current;
    const video7  = video7Ref.current;
    const video8  = video8Ref.current;
    const section = sectionRef.current;
    let initiated = false;

    // ── ENTRY ANIMATION — visible immediately on load ────────────
    // Start slightly below, fade + rise into place
    gsap.set(eyebrowRef.current,    { opacity: 0, y: 18, force3D: true });
    gsap.set(accentLineRef.current, { opacity: 0, scaleX: 0, transformOrigin: 'left center', force3D: true });
    gsap.set(headlineRef.current,   { opacity: 0, y: 28, force3D: true });
    gsap.set(descRef.current,       { opacity: 0, y: 20, force3D: true });
    gsap.set(ctaRef.current,        { opacity: 0, y: 14, force3D: true });

    // Navbar
    gsap.fromTo(
      navRef.current,
      { opacity: 0, y: -12 },
      { opacity: 1, y: 0, duration: 1.1, ease: 'power2.out', delay: 0.1, force3D: true },
    );

    // Staggered cinematic entry
    const entryTl = gsap.timeline({ delay: 0.25 });
    entryTl
      .to(eyebrowRef.current,    { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out', force3D: true })
      .to(accentLineRef.current, { opacity: 1, scaleX: 1, duration: 0.55, ease: 'power3.out', force3D: true }, '-=0.2')
      .to(headlineRef.current,   { opacity: 1, y: 0, duration: 0.85, ease: 'power3.out', force3D: true }, '-=0.3')
      .to(descRef.current,       { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out', force3D: true }, '-=0.4')
      .to(ctaRef.current,        { opacity: 1, y: 0, duration: 0.65, ease: 'back.out(1.4)', force3D: true }, '-=0.3');

    // ── Build scroll animation once ALL clips' metadata is ready ──
    // We scrub the clips back-to-back so the user perceives one
    // continuous film. The pinned scroll is split into equal segments,
    // one per clip. At each boundary we hard-swap which video is visible
    // — the last frame of the outgoing clip sits directly under the
    // first frame of the next, so the cut is invisible.
    const clips = [video, video2, video3, video4, video5, video6, video7, video8];

    const buildAnimation = () => {
      if (initiated) return;

      const durs = clips.map((v) => v.duration);
      // Need every duration before we can map scroll → time
      if (durs.some((d) => !d || isNaN(d))) return;

      initiated = true;

      const N = clips.length;

      // Now that clips overlap (CSS position:absolute), show ONLY clip 1
      // and hide the rest — otherwise the last clip in DOM paints on top.
      clips.forEach((v, i) => {
        v.currentTime = 0;
        gsap.set(v, { autoAlpha: i === 0 ? 1 : 0 });
      });

      // Decode clips LAZILY (one at a time, just before they're needed).
      // Priming all at once made the browser throttle the 3rd play() — that
      // clip stayed un-decoded and janked on arrival. A muted play→pause
      // primes the decoder so the clip scrubs smoothly when shown.
      // Keep only a small window of clips decoded at once. Browsers cap the
      // number of simultaneous video decoders — with 4+ clips the extra ones
      // fall back to slow software decode and jank. We attach+decode clips on
      // approach (ensureHot) and detach far-away ones to free the decoder
      // (coolClip). Durations were already captured above, so detaching is safe.
      const loaded = new Array(N).fill(false);
      const srcs   = clips.map((v) => v.getAttribute('src'));

      const ensureHot = (idx) => {
        if (idx < 0 || idx >= N || loaded[idx]) return;
        loaded[idx] = true;
        const vv = clips[idx];
        if (!vv.getAttribute('src')) { vv.setAttribute('src', srcs[idx]); vv.load(); }
        const pp = vv.play();
        if (pp && pp.then) pp.then(() => vv.pause()).catch(() => {});
        else vv.pause();
      };
      const coolClip = (idx) => {
        if (idx < 0 || idx >= N || !loaded[idx]) return;
        loaded[idx] = false;
        const vv = clips[idx];
        vv.pause();
        gsap.set(vv, { autoAlpha: 0 });
        vv.removeAttribute('src');
        vv.load();   // releases the video decoder
      };

      ensureHot(0);   // current clip now; the rest are managed on scroll

      // Track the currently-visible clip (for the dot-nav).
      let activeIndex = 0;

      // Dot-nav active state (updated only when the visible clip changes).
      let lastDot = -1;
      const updateDots = () => {
        if (activeIndex === lastDot) return;
        lastDot = activeIndex;
        dotRefs.current.forEach((el, k) => {
          if (el) el.classList.toggle('is-active', k === activeIndex);
        });
      };

      // quickSetters = fastest possible per-frame property writes.
      // Everything below is driven directly from scroll progress (no
      // scrub timelines) so video, text and hint all move together —
      // no double-smoothing, no desync, no lag.
      const setContentY       = gsap.quickSetter(heroContentRef.current, 'y', 'px');
      const setContentOpacity = gsap.quickSetter(heroContentRef.current, 'opacity');
      const setHintOpacity    = gsap.quickSetter(scrollHintRef.current,  'opacity');
      const setTransOpacity   = gsap.quickSetter(transitionRef.current,  'opacity');
      const setFogOpacity   = fogRef.current   ? gsap.quickSetter(fogRef.current,   'opacity') : () => {};
      const setBloomOpacity = bloomRef.current ? gsap.quickSetter(bloomRef.current, 'opacity') : () => {};
      const joinSetters = joinTextRefs.current.map((el) => (el ? {
        op:     gsap.quickSetter(el, 'opacity'),
        scale:  gsap.quickSetter(el, 'scale'),    /* materialize zoom-settle (no L/R slide) */
        blur:   gsap.quickSetter(el, '--jt-blur', 'px'),
        reveal: gsap.quickSetter(el, '--reveal'),
      } : null));
      const clipNoteSetters = clipNoteRefs.current.map((el) => (el ? {
        op: gsap.quickSetter(el, 'opacity'),
        y:  gsap.quickSetter(el, 'y', 'px'),
      } : null));
      const highlightSetters = highlightRefs.current.map((el) => (el ? gsap.quickSetter(el, 'opacity') : null));
      const joinButtons = joinTextRefs.current.map((el) => (el ? el.querySelector('.hero__join-cta') : null));
      const clipStopSetters = clipStopRefs.current.map((el) => (el ? gsap.quickSetter(el, 'opacity') : null));
      const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

      const TRANSITION = JOIN_TRANSITION;

      // Prime the fog overlay clip so it decodes (else it scrubs black).
      // Only needed in fog mode.
      const fog = fogRef.current;
      if (fog && TRANSITION === 'fog') {
        const pf = fog.play();
        if (pf && pf.then) pf.then(() => { fog.pause(); fog.currentTime = 0; }).catch(() => {});
      }

      // ── Chaptered timeline ──
      // The pinned scroll is split into CLIP phases (video scrubs at normal
      // speed) and JOIN phases (a slow "dwell": the video HOLDS on the
      // seamless boundary frame while the fog + chapter card play out slowly,
      // so the transition is appreciable and the text is readable).
      const BASE_DUR = 8;    // reference clip length (s) → ~750% of scroll
      const JOIN_W   = 0.16; // STOP dwell weight — short, so 1–2 scrolls clear it
      const CONT_W   = 0.12; // CONTINUOUS join weight (quick crossfade, no stop)

      // A boundary is a STOP only if it has a chapter card; otherwise it flows
      // straight on (continuous). e.g. clip 4 → 5 has no card → continuous.
      const isStop = (i) => !!JOIN_TEXTS[i];
      const joinW  = (i) => (isStop(i) ? JOIN_W : CONT_W);

      // Each clip's scroll weight is proportional to its DURATION, so every
      // clip scrubs at the SAME speed (seconds of footage per unit of scroll).
      const clipW = (i) => durs[i] / BASE_DUR;

      // A clip may carry a mid-clip STOP that pauses it partway through.
      const clipStopAt = (i) => (CLIP_STOPS[i] ? CLIP_STOPS[i].at : null);

      let phasesW = 0;
      for (let i = 0; i < N; i++) {
        phasesW += clipW(i);
        if (clipStopAt(i) != null) phasesW += JOIN_W;   // mid-clip stop dwell
        if (i < N - 1) phasesW += joinW(i);
      }

      const phases = [];
      {
        let cum = 0;
        const push = (type, idx, w, r0, r1) => {
          const start = cum / phasesW; cum += w;
          phases.push({ type, idx, start, end: cum / phasesW, r0, r1 });
        };
        for (let i = 0; i < N; i++) {
          const sp = clipStopAt(i);
          if (sp != null) {
            // play up to the stop frame, PAUSE, then play the rest.
            push('clip', i, clipW(i) * sp, 0, sp);
            push('clipstop', i, JOIN_W, sp, sp);
            push('clip', i, clipW(i) * (1 - sp), sp, 1);
          } else {
            push('clip', i, clipW(i), 0, 1);
          }
          if (i < N - 1) push('join', i, joinW(i));
        }
      }
      // Pin length: a weight of 1.0 (an 8s clip) = ~750% scroll.
      const END_PCT = Math.round(750 * phasesW);

      // Hero content clears during the first clip phase, before the first join.
      const contentFadeEnd   = phases[0].end * 0.6;
      const contentFadeStart = contentFadeEnd * 0.15;

      // Scrub a clip to a normalized position [0..1] of its duration.
      const scrubTo = (idx, t01) => {
        const vc = clips[idx];
        if (!vc || vc.readyState < 2) return;
        const target = clamp01(t01) * durs[idx];
        if (Math.abs(vc.currentTime - target) > 0.02) vc.currentTime = target;
      };

      // ── Auto-playing chapter cards (Persepolis "Bull Statues" style) ──
      // When the scroll ARRIVES at a stop, the matter plays itself: the video
      // freezes on its end frame and the lines waterfall in top-to-bottom on a
      // real-time timeline — it runs whether or not the user keeps scrolling.
      // The instant it's all in there's NO hold; it glides on into the next clip.
      let stopPlaying  = false;   // a stop autoplay is currently running
      let stopPlayedAt = -1;      // join idx that last autoplayed (prevents re-fire)
      let clipStopPlayedAt = -1;  // clip idx whose mid-clip stop last autoplayed
      let stopTl       = null;

      const STOP_LINE_SEL =
        '.hero__join-eyebrow-wrap, .hero__join-text-title, .hero__join-line,' +
        ' .hero__join-text-sub, .hero__join-cta';

      const stopLines = (i) => {
        const el = joinTextRefs.current[i];
        if (!el) return [];
        return Array.from(el.querySelectorAll(
          '.hero__join-eyebrow-wrap, .hero__join-text-title, .hero__join-line,' +
          ' .hero__join-text-sub, .hero__join-stats, .hero__join-cta'
        ));
      };

      const playStop = (joinIdx, prev, next) => {
        if (stopPlaying) return;
        const panel = joinTextRefs.current[joinIdx];
        const lines = stopLines(joinIdx);
        if (!panel || !lines.length) return;
        stopPlaying  = true;
        stopPlayedAt = joinIdx;

        // The outgoing clip has just finished. Under the dimming veil, cross
        // into the NEXT clip's FIRST frame and hold it there — so the card
        // introduces the upcoming video (it appears over the SECOND clip's
        // start, not over the frozen end of the first).
        scrubTo(prev, 1);
        scrubTo(next, 0);
        for (let k = 0; k < N; k++) gsap.set(clips[k], { autoAlpha: k === prev ? 1 : 0 });
        activeIndex = next;
        gsap.set(panel, { opacity: 1, scale: 1, '--jt-blur': '0px', '--reveal': 1 });
        gsap.set(lines, { opacity: 0, y: 30 });
        const hl  = highlightRefs.current[joinIdx];
        const btn = panel.querySelector('.hero__join-cta');

        // NO scroll lock. When the user is idle the matter auto-plays in full;
        // the moment they scroll, the stop branch snaps it fully in and hands
        // off to the held branch — so no scroll input is ever swallowed.
        stopTl = gsap.timeline({
          onComplete: () => { stopPlaying = false; },
        });
        stopTl.to(transitionRef.current, { opacity: 0.45, duration: 0.6, ease: 'power1.out' }, 0);
        // Crossfade outgoing → incoming first frame, masked by the veil.
        stopTl.to(clips[prev], { autoAlpha: 0, duration: 0.5, ease: 'power1.inOut' }, 0);
        stopTl.to(clips[next], { autoAlpha: 1, duration: 0.5, ease: 'power1.inOut' }, 0);
        if (hl) stopTl.fromTo(hl, { opacity: 0 }, { opacity: 1, duration: 0.6 }, 0.2);
        // WATERFALL — each line cascades in, top to bottom; then it holds.
        stopTl.to(lines, {
          opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.16,
        }, 0.15);
        if (btn) stopTl.call(() => { btn.style.pointerEvents = 'auto'; });
      };

      // Mid-clip stop: the SAME clip is held frozen on its stop frame while the
      // card waterfalls in. No crossfade (the clip stays put), then it WAITS.
      const playClipStop = (clipIdx) => {
        if (stopPlaying) return;
        const cs = CLIP_STOPS[clipIdx];
        const panel = clipStopRefs.current[clipIdx];
        if (!cs || !panel) return;
        const lines = Array.from(panel.querySelectorAll(STOP_LINE_SEL));
        if (!lines.length) return;
        stopPlaying = true;
        clipStopPlayedAt = clipIdx;

        scrubTo(clipIdx, cs.at);   // freeze on the stop frame
        for (let k = 0; k < N; k++) gsap.set(clips[k], { autoAlpha: k === clipIdx ? 1 : 0 });
        activeIndex = clipIdx;
        gsap.set(panel, { opacity: 1, scale: 1, '--jt-blur': '0px', '--reveal': 1 });
        gsap.set(lines, { opacity: 0, y: 30 });
        const btn = panel.querySelector('.hero__join-cta');

        // No scroll lock (see playStop) — first scroll snaps it in and moves on.
        stopTl = gsap.timeline({
          onComplete: () => { stopPlaying = false; },
        });
        stopTl.to(transitionRef.current, { opacity: 0.45, duration: 0.6, ease: 'power1.out' }, 0);
        stopTl.to(lines, {
          opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', stagger: 0.16,
        }, 0.15);
        if (btn) stopTl.call(() => { btn.style.pointerEvents = 'auto'; });
      };

      // Single pinned trigger drives the whole multi-clip sequence.
      const pinTrigger = ScrollTrigger.create({
        trigger: section,
        start: 'top top',
        end: `+=${END_PCT}%`,   // clip bodies ~750% each + slow join dwells
        pin: true,
        anticipatePin: 1,
        fastScrollEnd: true,
        preventOverlaps: true,
        onUpdate: (self) => {
          const p = self.progress;

          // Locate the current phase (clip or join) and its local progress t.
          let ph = phases[phases.length - 1];
          for (let i = 0; i < phases.length; i++) {
            if (p <= phases[i].end) { ph = phases[i]; break; }
          }
          const t = clamp01((p - ph.start) / (ph.end - ph.start));

          // Hero content fades out during the first clip phase.
          const cf = clamp01((p - contentFadeStart) / (contentFadeEnd - contentFadeStart));
          setContentY(-80 * cf);
          setContentOpacity(1 - cf);
          setHintOpacity(1 - clamp01(p / 0.04));

          let activeJoin = -1, joinTextOp = 0;
          let activeClip = -1, clipNoteOp = 0;
          let activeClipStop = -1, clipStopOp = 0;

          if (ph.type === 'clip') {
            // ── Normal-speed clip scrub (over this phase's sub-range r0..r1) ──
            const seg = ph.idx;
            const r0 = ph.r0 ?? 0, r1 = ph.r1 ?? 1;
            ensureHot(seg - 1); ensureHot(seg); ensureHot(seg + 1);
            for (let k = 0; k < N; k++) if (k < seg - 1 || k > seg + 1) coolClip(k);
            // Force exclusivity EVERY frame — only this clip is visible. (Fixes
            // a second clip bleeding through on reverse-scroll / after a jump.)
            for (let k = 0; k < N; k++) gsap.set(clips[k], { autoAlpha: k === seg ? 1 : 0 });
            activeIndex = seg;
            stopPlayedAt = -1;   // back on a clip → re-arm the next stop's autoplay
            // Re-arm this clip's mid-stop only while BEFORE it (first sub-clip),
            // so jitter at the stop boundary can't re-fire it.
            if (r0 === 0) clipStopPlayedAt = -1;
            scrubTo(seg, r0 + t * (r1 - r0));
            setTransOpacity(0);
            setFogOpacity(0);
            setBloomOpacity(0);
            // Feature callout opacity.
            activeClip = seg;
            const note = CLIP_NOTES[seg];
            if (note && note.late) {
              // Appears in the LATTER part of the clip and clears right as it
              // ends — so it reads near the clip's end, before the next stop.
              const fIn  = clamp01((t - 0.5) / 0.22);   // fade in across 50%→72%
              const fOut = clamp01((1 - t) / 0.1);      // fade out over the last 10%
              clipNoteOp = Math.min(fIn, fOut);
            } else if (note && note.fadeOut) {
              // Fade in over ~1s, hold, then fade out over ~1.5s ending at
              // `fadeOut` seconds of the clip — then stay hidden.
              const vt = t * durs[seg];                          // video-time (s)
              const fIn  = clamp01(vt / 1.0);
              const fOut = 1 - clamp01((vt - (note.fadeOut - 1.5)) / 1.5);
              clipNoteOp = Math.max(0, Math.min(fIn, fOut));
            } else {
              // Default: fade in first 20%, hold, fade out last 20%.
              clipNoteOp = clamp01(Math.min(t, 1 - t) / 0.2);
            }
          } else if (ph.type === 'clipstop') {
            // ── Mid-clip STOP — the clip is frozen on its stop frame while a
            //    card waterfalls in (auto-played on arrival), then it WAITS;
            //    the first scroll fades the card and the clip plays on. ──
            const seg = ph.idx;
            const cs = CLIP_STOPS[seg];
            ensureHot(seg - 1); ensureHot(seg); ensureHot(seg + 1);
            for (let k = 0; k < N; k++) if (k < seg - 1 || k > seg + 1) coolClip(k);
            for (let k = 0; k < N; k++) gsap.set(clips[k], { autoAlpha: k === seg ? 1 : 0 });
            activeIndex = seg;
            scrubTo(seg, cs.at);   // hold the stop frame

            if (!stopPlaying && clipStopPlayedAt !== seg && self.direction === 1 && t > 0.01) {
              playClipStop(seg);
              return;   // the timeline owns the screen now
            }
            // First scroll → snap the waterfall in and release to the held branch.
            if (stopPlaying) { if (stopTl) stopTl.progress(1); stopPlaying = false; }

            // Played already → card held; the first scroll fades it and lifts
            // the dim, then the clip's second half plays. Holds if idle.
            const xf = clamp01((t - 0.08) / 0.92);
            setTransOpacity(0.45 * (1 - xf));
            setFogOpacity(0);
            setBloomOpacity(0);
            setContentOpacity(0);
            setContentY(-80);
            activeClipStop = seg;
            clipStopOp = 1 - xf;
          } else {
            const prev = ph.idx, next = ph.idx + 1;
            ensureHot(prev); ensureHot(next);
            for (let k = 0; k < N; k++) if (k !== prev && k !== next) coolClip(k);
            scrubTo(prev, 1);   // outgoing clip held on its last frame
            scrubTo(next, 0);   // incoming clip held on its first frame

            if (isStop(ph.idx)) {
              // ── STOP — auto-playing chapter card. The instant we arrive
              //    (scrolling forward), hand off to a real-time timeline that
              //    waterfalls the matter in; it runs with or without further
              //    scroll, then glides on. See playStop() above. ──
              if (!stopPlaying && stopPlayedAt !== ph.idx && self.direction === 1 && t > 0.01) {
                playStop(ph.idx, prev, next);
                return;   // the timeline owns the screen now
              }
              // onUpdate fires only when the user scrolls — so reaching here
              // means they want to move on: snap the waterfall fully in and
              // release to the held branch (no scroll input is swallowed).
              if (stopPlaying) { if (stopTl) stopTl.progress(1); stopPlaying = false; }

              // Played already → the card is fully in over the NEXT clip's
              // first frame, and now WAITS here. The waterfall already gave the
              // reading time, so the FIRST scroll should move on: after a tiny
              // margin the card fades + the dim lifts, revealing the next clip,
              // which then plays in its own phase. The next clip stays shown the
              // whole time (the card sits over its first frame). Holds if idle.
              const xf = clamp01((t - 0.08) / 0.92);
              for (let k = 0; k < N; k++) gsap.set(clips[k], { autoAlpha: k === next ? 1 : 0 });
              activeIndex = next;
              setTransOpacity(0.45 * (1 - xf));   // keep the dim while reading, lift at the end
              setFogOpacity(0);
              setBloomOpacity(0);
              setContentOpacity(0);
              setContentY(-80);
              activeJoin = ph.idx;
              joinTextOp = 1 - xf;                // card held in; fades as we move on
            } else {
              // ── CONTINUOUS: no stop — quick crossfade straight into the
              //    next clip, no chapter card, no darken. ──
              gsap.set(clips[prev], { autoAlpha: 1 - t });
              gsap.set(clips[next], { autoAlpha: t });
              for (let k = 0; k < N; k++) {
                if (k !== prev && k !== next) gsap.set(clips[k], { autoAlpha: 0 });
              }
              activeIndex = t < 0.5 ? prev : next;
              setTransOpacity(0);
              setFogOpacity(0);
              setBloomOpacity(0);
              // activeJoin stays -1 → no card shown
            }
          }

          // Hold/fade the active join's panel. The waterfall (playStop) handles
          // the per-line entrance; here we only carry the settled panel through
          // its WAIT and fade it out as the user scrolls across to the next clip.
          for (let j = 0; j < joinSetters.length; j++) {
            const hs = highlightSetters[j];
            if (hs) hs(j === activeJoin ? joinTextOp : 0);
            const btn = joinButtons[j];
            if (btn) btn.style.pointerEvents = (j === activeJoin && joinTextOp > 0.55) ? 'auto' : 'none';
            const s = joinSetters[j];
            if (!s) continue;
            if (j === activeJoin) {
              s.op(joinTextOp);
              s.scale(1);
              s.blur(0);
              s.reveal(1);
            } else {
              s.op(0);
              s.reveal(0);
            }
          }

          // Apply the active clip's feature callout (rise + fade); hide rest.
          for (let c = 0; c < clipNoteSetters.length; c++) {
            const s = clipNoteSetters[c];
            if (!s) continue;
            if (c === activeClip) {
              s.op(clipNoteOp);
              s.y((1 - clipNoteOp) * 24);
            } else {
              s.op(0);
            }
          }

          // Hold/fade the active mid-clip stop card (lines are waterfalled by
          // playClipStop; here we only carry/fade the whole panel).
          for (let c = 0; c < clipStopSetters.length; c++) {
            const s = clipStopSetters[c];
            if (!s) continue;
            s(c === activeClipStop ? clipStopOp : 0);
          }

          updateDots();   // highlight the dot for the current clip
        },
      });
      triggersRef.current.push(pinTrigger);

      // Click a dot → jump DIRECTLY to the STOP that introduces that clip
      // (where its text shows), then it plays on. No scrubbing through.
      scrollToClipRef.current = (d) => {
        // the stop before clip d shows its card; clip 0 has no stop → its start
        let ph = phases.find((p) => p.type === 'join' && p.idx === d - 1);
        if (!ph) ph = phases.find((p) => p.type === 'clip' && p.idx === d);
        if (!ph) return;
        ensureHot(d - 1); ensureHot(d); ensureHot(d + 1);   // decode target first
        const prog = Math.min(0.999, ph.start + 0.004);     // start of the text stop
        const target = pinTrigger.start + prog * (pinTrigger.end - pinTrigger.start);
        lenis.scrollTo(target, { immediate: true, force: true });
      };
    };

    if (clips.every((v) => v.readyState >= 1)) buildAnimation();
    clips.forEach((v) => v.addEventListener('loadedmetadata', buildAnimation));

    return () => {
      clips.forEach((v) => v.removeEventListener('loadedmetadata', buildAnimation));
      triggersRef.current.forEach((t) => t && t.kill());
      triggersRef.current = [];
      if (rafFnRef.current) gsap.ticker.remove(rafFnRef.current);
      if (lenisRef.current) lenisRef.current.destroy();
    };
  }, []);

  // Stop / resume Lenis when mobile menu opens/closes
  useEffect(() => {
    if (!lenisRef.current) return;
    if (menuOpen) {
      lenisRef.current.stop();
      document.body.style.overflow = 'hidden';
    } else {
      lenisRef.current.start();
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      {/* ── MOBILE MENU OVERLAY ─────────────────────────────────── */}
      <div
        className={`mobile-menu${menuOpen ? ' mobile-menu--open' : ''}`}
        aria-hidden={!menuOpen}
        role="dialog"
        aria-modal="true"
      >
        {/* Close hit-area (tap outside links) */}
        <div className="mobile-menu__backdrop" onClick={closeMenu} />

        {/* Menu content */}
        <nav className="mobile-menu__nav">
          {/* Logo inside menu */}
          <div className="mobile-menu__logo">
            <img src="/assests/MYACCESS White.svg" alt="MYACCESS" className="mobile-menu__logo-img" />
          </div>

          <ul className="mobile-menu__links">
            <li className="mobile-menu__item">
              <a href="#models" onClick={closeMenu}>Series&nbsp;1</a>
            </li>
            <li className="mobile-menu__item">
              <a href="#tech" onClick={closeMenu}>Technology</a>
            </li>
            <li className="mobile-menu__item">
              <a href="#reserve" onClick={closeMenu}>Reserve</a>
            </li>
          </ul>

          {/* CTA inside menu */}
          <div className="mobile-menu__cta">
            <a href="#reserve" className="mobile-menu__reserve-btn" onClick={closeMenu}>
              Reserve Now
            </a>
          </div>
        </nav>
      </div>

      {/* ── NAVBAR ─────────────────────────────────────────────── */}
      <nav ref={navRef} className="evpd-nav">
        <div className="evpd-nav__logo">
          <img
            src="/assests/MYACCESS White.svg"
            alt="MYACCESS logo"
            className="evpd-nav__logo-image"
          />
        </div>

        {/* Desktop links */}
        <ul className="evpd-nav__links">
          <li><a href="#models">Series&nbsp;1</a></li>
          <li><a href="#tech">Technology</a></li>
          <li><a href="#reserve" className="nav-reserve">Reserve</a></li>
        </ul>

        {/* Hamburger — mobile only */}
        <button
          className={`nav-hamburger${menuOpen ? ' nav-hamburger--open' : ''}`}
          onClick={() => setMenuOpen(o => !o)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
        >
          <span className="nav-hamburger__line" />
          <span className="nav-hamburger__line" />
          <span className="nav-hamburger__line" />
        </button>
      </nav>

      {/* ── HERO SECTION ───────────────────────────────────────── */}
      <section ref={sectionRef} className="hero">

        {/* Video — full screen, crystal clear. Two clips stacked and
            scrubbed back-to-back so they read as one continuous film. */}
        <div
          ref={videoWrapRef}
          className="hero__video-wrap"
          onContextMenu={(e) => e.preventDefault()}  /* block right-click "Save video as" */
        >
          {/* Clip 1 — plays first */}
          <video
            ref={videoRef}
            className="hero__video hero__video--first"
            src="/assests/video1.mp4"
            preload="auto"
            muted
            playsInline
          />
          {/* Clip 2 */}
          <video
            ref={video2Ref}
            className="hero__video hero__video--second"
            src="/assests/video2.mp4"
            preload="auto"
            muted
            playsInline
          />
          {/* Clip 3 */}
          <video
            ref={video3Ref}
            className="hero__video hero__video--third"
            src="/assests/video3.mp4"
            preload="auto"
            muted
            playsInline
          />
          {/* Clip 4 */}
          <video
            ref={video4Ref}
            className="hero__video hero__video--fourth"
            src="/assests/video4.mp4"
            preload="auto"
            muted
            playsInline
          />
          {/* Clip 5 — continues straight from clip 4 (no stop) */}
          <video
            ref={video5Ref}
            className="hero__video hero__video--fifth"
            src="/assests/video5.mp4"
            preload="auto"
            muted
            playsInline
          />
          {/* Clip 6 */}
          <video
            ref={video6Ref}
            className="hero__video hero__video--sixth"
            src="/assests/video6.mp4"
            preload="auto"
            muted
            playsInline
          />
          {/* Clip 7 */}
          <video
            ref={video7Ref}
            className="hero__video hero__video--seventh"
            src="/assests/video7.mp4"
            preload="auto"
            muted
            playsInline
          />
          {/* Clip 8 */}
          <video
            ref={video8Ref}
            className="hero__video hero__video--eighth"
            src="/assests/video8.mp4"
            preload="auto"
            muted
            playsInline
          />
        </div>

        {/* Clip-join transition overlay — darkens to mask the swap between clips */}
        <div ref={transitionRef} className="hero__transition" aria-hidden="true" />

        {/* Fog sweep — rolls in/out across the clip join (screen-blended) */}
        {JOIN_TRANSITION === 'fog' && (
          <video
            ref={fogRef}
            className="hero__fog"
            src="/assests/fog.mp4"
            preload="auto"
            muted
            playsInline
            aria-hidden="true"
          />
        )}

        {/* Light bloom — bright showroom-glare wash across the clip join */}
        {JOIN_TRANSITION === 'bloom' && (
          <div ref={bloomRef} className="hero__bloom" aria-hidden="true" />
        )}

        {/* Editorial text revealed during each clip join */}
        {JOIN_TEXTS.map((t, i) => (t ? (
          <div
            key={i}
            ref={(el) => { joinTextRefs.current[i] = el; }}
            className={`hero__join-text hero__join-text--${t.align || 'center'}`}
          >
            <div className="hero__join-inner">
              <span className="hero__join-bgnum" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
              <div className="hero__join-eyebrow-wrap">
                <span className="hero__join-eyebrow-dot" aria-hidden="true" />
                <span className="hero__join-eyebrow">{t.eyebrow}</span>
              </div>
              <span className="hero__join-text-title">{t.title}</span>
              <span className="hero__join-line" aria-hidden="true" />
              <span className="hero__join-text-sub">{t.sub}</span>
              {t.cta && (
                <button className="hero__join-cta" type="button">
                  <span>{t.cta}</span>
                  <span className="hero__join-cta-arrow" aria-hidden="true">→</span>
                </button>
              )}
            </div>
          </div>
        ) : null))}

        {/* Highlight indicator pointing at the subject during each join */}
        {JOIN_TEXTS.map((t, i) => (t && t.highlight ? (
          <div
            key={i}
            ref={(el) => { highlightRefs.current[i] = el; }}
            className="hero__join-highlight"
            style={{ left: t.highlight.x, top: t.highlight.y }}
            aria-hidden="true"
          />
        ) : null))}

        {/* Feature callouts shown while scrolling through each clip */}
        {CLIP_NOTES.map((note, i) => (note ? (
          <div
            key={i}
            ref={(el) => { clipNoteRefs.current[i] = el; }}
            className="hero__clip-note"
          >
            <div className="hero__clip-note-eyebrow">
              <span className="hero__clip-note-dot" aria-hidden="true" />
              {note.eyebrow}
            </div>
            <h3 className="hero__clip-note-title">{note.title}</h3>
            <p className="hero__clip-note-sub">{note.sub}</p>
          </div>
        ) : null))}

        {/* Mid-clip STOP cards — full chapter card shown while a clip is paused */}
        {CLIP_STOPS.map((c, i) => (c ? (
          <div
            key={i}
            ref={(el) => { clipStopRefs.current[i] = el; }}
            className={`hero__join-text hero__join-text--${c.align || 'center'}`}
          >
            <div className="hero__join-inner">
              <span className="hero__join-bgnum" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
              <div className="hero__join-eyebrow-wrap">
                <span className="hero__join-eyebrow-dot" aria-hidden="true" />
                <span className="hero__join-eyebrow">{c.eyebrow}</span>
              </div>
              <span className="hero__join-text-title">{c.title}</span>
              <span className="hero__join-line" aria-hidden="true" />
              <span className="hero__join-text-sub">{c.sub}</span>
              {c.cta && (
                <button className="hero__join-cta" type="button">
                  <span>{c.cta}</span>
                  <span className="hero__join-cta-arrow" aria-hidden="true">→</span>
                </button>
              )}
            </div>
          </div>
        ) : null))}

        {/* Scene dot-navigation — highlights the current clip */}
        <div className="hero__dotnav" aria-hidden="true">
          {DOT_NAMES.map((_, d) => (
            <span
              key={d}
              ref={(el) => { dotRefs.current[d] = el; }}
              className="hero__dot"
              role="button"
              tabIndex={0}
              onClick={() => scrollToClipRef.current && scrollToClipRef.current(d)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scrollToClipRef.current && scrollToClipRef.current(d); } }}
            >
              <span className="hero__dot-label">{DOT_NAMES[d]}</span>
            </span>
          ))}
        </div>

        {/* Subtle left-edge vignette — keeps text readable without covering scooter */}
        <div className="hero__vignette" aria-hidden="true" />

        {/* ── PRIMARY HERO CONTENT — lower-left, visible on load ── */}
        <div ref={heroContentRef} className="hero__content">

          {/* Eyebrow */}
          <div ref={eyebrowRef} className="hero__eyebrow-wrap">
            <span className="hero__eyebrow-dot" aria-hidden="true" />
            <span className="hero__eyebrow">EV Vehicle&nbsp;&nbsp;&nbsp;·&nbsp;&nbsp;2026</span>
          </div>

          {/* Accent line */}
          <div ref={accentLineRef} className="hero__accent-line" aria-hidden="true" />

          {/* Headline */}
          <h1 ref={headlineRef} className="hero__headline">
            The Future<br />Moves First.
          </h1>

          {/* Description */}
          <p ref={descRef} className="hero__desc">
            India's next-generation electric mobility platform —<br />
            engineered for performance, intelligence and sustainability.
          </p>

          {/* CTA Buttons */}
          <div ref={ctaRef} className="hero__cta">
            <button className="btn-primary">
              <span>Reserve Now</span>
              <svg className="btn-arrow" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button className="btn-ghost">Explore Technology</button>
          </div>

          {/* Micro specs row */}
          <div className="hero__specs">
            <div className="hero__spec">
              <span className="hero__spec-val">0–60</span>
              <span className="hero__spec-label">km/h in 3.8s</span>
            </div>
            <div className="hero__spec-divider" aria-hidden="true" />
            <div className="hero__spec">
              <span className="hero__spec-val">160+</span>
              <span className="hero__spec-label">km range</span>
            </div>
            <div className="hero__spec-divider" aria-hidden="true" />
            <div className="hero__spec">
              <span className="hero__spec-val">IP67</span>
              <span className="hero__spec-label">weatherproof</span>
            </div>
          </div>
        </div>

        {/* Watermark cover badge */}
        <div className="hero__wm-cover">
          
        </div>

        {/* Scroll hint */}
        <div ref={scrollHintRef} className="hero__scroll-hint">
          <span>Scroll to reveal</span>
          <div className="hero__scroll-track">
            <div className="hero__scroll-dot" />
          </div>
        </div>

      </section>
    </>
  );
};

export default HeroSection;